import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { InvoiceService } from '@/services/invoice/invoiceService';
import { Invoice, CreateInvoiceRequest, DeleteInvoiceFileRequest, AddInvoiceFileRequest } from '@/types/payment';
import { FileObject } from '@/types/shared/file';

export const useInvoice = () => {
  const queryClient = useQueryClient();

  // Get all invoices
  const useGetAllInvoices = () => {
    return useQuery({
      queryKey: ['invoices'],
      queryFn: () => InvoiceService.getAllInvoices(),
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get single invoice
  const useGetInvoice = (id: string) => {
    return useQuery({
      queryKey: ['invoice', id],
      queryFn: () => InvoiceService.getInvoice(id),
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });
  };

  // Get invoices by payment ID
  const useGetInvoicesByPaymentId = (paymentId: string) => {
    return useQuery({
      queryKey: ['invoices', 'payment', paymentId],
      queryFn: () => InvoiceService.getInvoicesByPaymentId(paymentId),
      enabled: !!paymentId,
      staleTime: 5 * 60 * 1000,
    });
  };


  // Get all invoices extended with listing and payment info
  const useGetAllInvoicesExtended = (page: number = 1, limit: number = 10, status?: string, search?: string) => {
    return useQuery({
      queryKey: ['invoices', 'extended', page, limit, status, search],
      queryFn: () => InvoiceService.getAllInvoicesExtended(page, limit, status, search),
      staleTime: 0 * 60 * 1000,
      gcTime: 0 * 60 * 1000,
    });
  };

  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: ({ request, replaceExisting }: { request: CreateInvoiceRequest; replaceExisting?: boolean }) =>
      InvoiceService.createInvoice(request, replaceExisting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', 'extended'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-card'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-cards'] });
    },
  });

  // Update invoice mutation
  const updateInvoiceMutation = useMutation({
    mutationFn: (data: Partial<Invoice> & { id: string }) =>
      InvoiceService.updateInvoice(data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: string) => InvoiceService.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Delete invoice file mutation
  const deleteInvoiceFileMutation = useMutation({
    mutationFn: (request: DeleteInvoiceFileRequest) => InvoiceService.deleteInvoiceFile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Add invoice file mutation
  const addInvoiceFileMutation = useMutation({
    mutationFn: (request: AddInvoiceFileRequest) => InvoiceService.addInvoiceFile(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['listing'] });
    },
  });

  // Check existing invoice by listing ID
  const checkExistingInvoiceByListingId = async (listingId: string) => {
    return await InvoiceService.checkExistingInvoiceByListingId(listingId);
  };

  return {
    // Queries
    useGetAllInvoices,
    useGetInvoice,
    useGetInvoicesByPaymentId,
    useGetAllInvoicesExtended,
    
    // Utility functions
    checkExistingInvoiceByListingId,
    
    // Mutations
    createInvoice: createInvoiceMutation.mutate,
    createInvoiceAsync: createInvoiceMutation.mutateAsync,
    isCreatingInvoice: createInvoiceMutation.isPending,
    createInvoiceError: createInvoiceMutation.error,
    resetCreateInvoice: createInvoiceMutation.reset,
    
    updateInvoice: updateInvoiceMutation.mutate,
    updateInvoiceAsync: updateInvoiceMutation.mutateAsync,
    isUpdatingInvoice: updateInvoiceMutation.isPending,
    updateInvoiceError: updateInvoiceMutation.error,
    resetUpdateInvoice: updateInvoiceMutation.reset,
    
    deleteInvoice: deleteInvoiceMutation.mutate,
    deleteInvoiceAsync: deleteInvoiceMutation.mutateAsync,
    isDeletingInvoice: deleteInvoiceMutation.isPending,
    deleteInvoiceError: deleteInvoiceMutation.error,
    resetDeleteInvoice: deleteInvoiceMutation.reset,
    
    // File operations
    deleteInvoiceFile: deleteInvoiceFileMutation.mutate,
    deleteInvoiceFileAsync: deleteInvoiceFileMutation.mutateAsync,
    isDeletingInvoiceFile: deleteInvoiceFileMutation.isPending,
    deleteInvoiceFileError: deleteInvoiceFileMutation.error,
    resetDeleteInvoiceFile: deleteInvoiceFileMutation.reset,
    
    addInvoiceFile: addInvoiceFileMutation.mutate,
    addInvoiceFileAsync: addInvoiceFileMutation.mutateAsync,
    isAddingInvoiceFile: addInvoiceFileMutation.isPending,
    addInvoiceFileError: addInvoiceFileMutation.error,
    resetAddInvoiceFile: addInvoiceFileMutation.reset,
  };
};
